import React, { useState, useEffect } from 'react';
import { withTranslation } from "react-i18next"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import toastr from "toastr"
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Spinner
} from "reactstrap"
import { Add } from "@mui/icons-material"
import FormSelectCompany from "../../components/Common/form-select-company"
import { getCompanies } from "../../helpers/utils"
import {
  apiBuscarMenuHierarquia,
  apiCriarMenu,
  apiEditarMenu,
  apiExcluirMenu,
  apiCriarAgrupamento,
  apiEditarAgrupamento,
  apiExcluirAgrupamento,
  apiCriarCardapio,
  apiEditarCardapio,
  apiExcluirCardapio,
  apiBuscarIcone
} from "../../helpers/backend_helper"
import { ConfirmationModal } from "./confirmation-modal"
import { TreeNode } from "./tree-node"
import { getDialogConfig } from "./get-dialog-config"
import { translationMenuManagement } from "./utils/translation"
import FormInputFile from "../../components/Common/form-input-file"
import usePermission from "../../helpers/permission/usePermission"

const MenuManagement = (props) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [menuData, setMenuData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [currentDialog, setCurrentDialog] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const [loadingMenus, setLoadingMenus] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const translation = translationMenuManagement(props)

  const usePermissionEdit = usePermission("menuDispositivo_editar")
  const usePermissionInsert = usePermission("menuDispositivo_inserir")

  useEffect(() => {
    if (props.user) {
      const tmpCompanies = getCompanies(props.user);
      setCompanies(tmpCompanies);

      if (tmpCompanies && tmpCompanies.length > 0) {
        const firstCompanyId = tmpCompanies[0].value;
        if (firstCompanyId) {
          setSelectedCompany(firstCompanyId);
        }
      }
    }

    return () => {
      setCompanies([]);
      setSelectedCompany(null);
    }
  }, [props.user]);

  useEffect(() => {
    return () => {
      menuData.forEach(menu => {
        if (menu.icone && menu.icone.startsWith('blob:')) {
          URL.revokeObjectURL(menu.icone);
        }
      });
    };
  }, [menuData]);

  useEffect(() => {
    if (selectedCompany) {
      loadMenuHierarchy(selectedCompany);
    } else {
      setMenuData([]);
      setLoadingMenus(false);
    }
  }, [selectedCompany]);

  const loadMenuHierarchy = async (empresaId) => {
    try {
      setLoadingMenus(true);
      const response = await apiBuscarMenuHierarquia({ idEmpresa: empresaId })
      if(response.length === 0) setMenuData([])

      const formattedData = await transformApiDataToTree(response);
      setMenuData(formattedData);
    } catch (error) {
      toastr.error(translation.error);
      setMenuData([]);
    } finally {
      setLoadingMenus(false);
    }
  };

  const handleToggleNode = (nodeId, nodeType) => {
    const nodeKey = `${nodeType}_${nodeId}`;
    
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeKey)) {
        newSet.delete(nodeKey);
      } else {
        newSet.add(nodeKey);
      }

      return newSet;
    });

    setMenuData(prevData => {
      const updateNode = (nodes) => {
        return nodes.map(node => {
          if (node.id === nodeId && node.type === nodeType) {
            return { ...node, expanded: !node.expanded };
          }
          
          if (node.children && node.children.length > 0) {
            return {
              ...node,
              children: updateNode(node.children)
            };
          }
          
          return node;
        });
      };

      return updateNode(prevData);
    });
  };

  const transformApiDataToTree = async apiData => {
    const menus = await Promise.all(
      apiData.map(async menu => {
        let iconeUrl = null

        if (menu.icone) {
          try {
            const blob = await apiBuscarIcone({ idMenu: menu.id });

            if (blob && blob.size > 0) {
              iconeUrl = URL.createObjectURL(blob);
            } else {
              console.warn(`Ícone do menu ${menu.id} retornou um blob inválido.`);
            }
          } catch (error) {
            console.error(`Erro ao buscar e processar o ícone para o menu ${menu.id}:`, error);
          }
        }

        return {
          id: menu.id,
          nome: menu.nome,
          icone: iconeUrl,
          ordem: Number(menu?.ordem),
          type: "menu",
          expanded: expandedNodes.has(`menu_${menu.id}`),
          children: (menu?.agrupamentos || [])
            .map(grupo => ({
              id: grupo.id,
              nome: grupo.nome,
              ordem: Number(grupo.ordem),
              type: "group",
              expanded: expandedNodes.has(`group_${grupo.id}`),
              parentId: menu.id,
              children: (grupo?.cardapios || [])
                .map(item => ({
                  id: item.id,
                  nome: item.produto?.nome || item.codigo_produto,
                  preco: item.produto?.preco,
                  ordem: Number(item.ordem),
                  type: "item",
                  parentId: grupo.id,
                  produto: item.produto,
                }))
                .sort((a, b) => a.ordem - b.ordem),
            }))
            .sort((a, b) => a.ordem - b.ordem),
        }
      })
    )

    return menus.sort((a, b) => a.ordem - b.ordem)
  }

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
  };


  const handleCreate = (parentNode) => {
    let dialogType = 'menu';

    if (parentNode) {
      if (parentNode.type === 'menu') {
        dialogType = 'group';
      } else if (parentNode.type === 'group') {
        dialogType = 'item';
      }
    }

    const config = getDialogConfig(dialogType);
    setCurrentDialog({ type: dialogType, parentNode, mode: 'create', ...config });
    setFormValues({ ordem: 1 });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleEdit = (node) => {
    const config = getDialogConfig(node.type);
    setCurrentDialog({
      type: node.type,
      node,
      mode: 'edit',
      ...config,
      title: `Editar ${config.title}`
    });

    const initialValues = {
      nome: node.nome,
      ordem: node.ordem || 1,
      codigo_produto: node.codigo_produto ?? node.nome
    };

    if (node.type === 'menu' && node.icone) {
      const base64WithPrefix = `data:image/png;base64,${node.icone}`;
      initialValues.icone = {
        nome: 'icone.png',
        base64: base64WithPrefix,
        base64SemPrefixo: node.icone
      };
    }

    setFormValues(initialValues);
    setFormErrors({});
    setModalOpen(true);
  };

  const handleDelete = (node) => {
    setItemToDelete(node);
    setConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoadingDelete(true);

      if (itemToDelete.type === 'menu') {
        await apiExcluirMenu({ id: itemToDelete.id });

        setExpandedNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemToDelete.id);
          if (itemToDelete.children) {
            itemToDelete.children.forEach(child => {
              newSet.delete(child.id);
              if (child.children) {
                child.children.forEach(grandChild => newSet.delete(grandChild.id));
              }
            });
          }
          return newSet;
        });

      } else if (itemToDelete.type === 'group') {
        await apiExcluirAgrupamento({ idMenu: itemToDelete.parentId, id: itemToDelete.id });

        setExpandedNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemToDelete.id);
          if (itemToDelete.children) {
            itemToDelete.children.forEach(child => newSet.delete(child.id));
          }
          return newSet;
        });

      } else if (itemToDelete.type === 'item') {
        await apiExcluirCardapio({ idAgrupamento: itemToDelete.parentId, id: itemToDelete.id });
      }

      toastr.success(translation.confirmDelete);
      setConfirmModalOpen(false);
      setItemToDelete(null);

      await loadMenuHierarchy(selectedCompany);

    } catch (error) {
      const message = error?.response?.data?.[0]?.message || translation.dialogError;
      toastr.error(message);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleFormChange = (fieldName, value) => {
    setFormValues(prev => ({ ...prev, [fieldName]: value }));

    if (formErrors[fieldName]) {
      setFormErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!currentDialog) return false;

    currentDialog.fields.forEach(field => {
      if (field.required && (!formValues[field.name] || formValues[field.name].toString().trim() === '')) {
        errors[field.name] = `${field.label} é obrigatório`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoadingSubmit(true);

      const payload = { ...formValues };

      if (payload.icone && payload.icone.base64SemPrefixo) {
        payload.icone = payload.icone.base64SemPrefixo;
      } else if (payload.icone === null || payload.icone === undefined) {
        delete payload.icone;
      }

      if (currentDialog.mode === 'create') {
        if (currentDialog.type === 'menu') {
          payload.id_empresa = parseInt(selectedCompany);
          await apiCriarMenu(payload);
        } else if (currentDialog.type === 'group') {
          await apiCriarAgrupamento({
            idMenu: currentDialog.parentNode.id,
            ...payload
          });
        } else if (currentDialog.type === 'item') {
          await apiCriarCardapio({
            idAgrupamento: currentDialog.parentNode.id,
            ...payload
          });
        }
        toastr.success(translation.confirmSuccess);
      } else {
        if (currentDialog.type === 'menu') {
          payload.id_empresa = parseInt(selectedCompany);
          await apiEditarMenu({
            id: currentDialog.node.id,
            ...payload
          });
        } else if (currentDialog.type === 'group') {
          await apiEditarAgrupamento({
            idMenu: currentDialog.node.parentId,
            id: currentDialog.node.id,
            ...payload
          });
        } else if (currentDialog.type === 'item') {
          await apiEditarCardapio({
            idAgrupamento: currentDialog.node.parentId,
            id: currentDialog.node.id,
            ...payload
          });
        }
        toastr.success(translation.confirmSuccess);
      }

      setModalOpen(false);
      setCurrentDialog(null);

      await loadMenuHierarchy(selectedCompany);

    } catch (error) {
      const message = error?.response?.data?.[0]?.message || translation.saveError;
      toastr.error(message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const renderFormField = (field) => {

    const fieldProps = {
      id: field.name,
      name: field.name,
      value: formValues[field.name] || '',
      onChange: (e) => handleFormChange(field.name, e.target.value),
      className: formErrors[field.name] ? 'is-invalid' : '',
      placeholder: field.placeholder,
      disabled: loadingSubmit
    };

    return (
      <FormGroup key={field.name}>
        <Label for={field.name}>
          {field.label}
          {field.required && <span className="text-danger"> *</span>}
        </Label>
        {field.type === 'file'
          ? <FormInputFile
            key={field.name}
            attribute={field.name}
            value={formValues[field.name]}
            setValue={(value) => handleFormChange(field.name, value)}
            required={field.required}
            disabled={loadingSubmit}
            extension="image/png, image/jpeg"
            labelColumns={12}
          />
          : <Input {...fieldProps} type={field.type || 'text'} />
        }

        {formErrors[field.name] && (
          <div className="invalid-feedback d-block">
            {formErrors[field.name]}
          </div>
        )}
      </FormGroup>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">{translation.title}</h4>
              </CardHeader>
              <CardBody>
                <Row className="mb-4">
                  <Col md={6}>
                    <FormSelectCompany
                      attribute="empresa"
                      options={companies.filter(c => c.value)}
                      value={selectedCompany}
                      setValue={handleCompanyChange}
                      description={translation.description}
                      placeholder={translation.placeholder}
                      required={true}
                    />
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <Button
                      color="primary"
                      onClick={() => handleCreate()}
                      className="mb-4"
                      disabled={!selectedCompany || loadingMenus || !usePermissionEdit || !usePermissionInsert}
                    >
                      <Add className="me-1" />{translation.create}
                    </Button>
                  </Col>
                </Row>

                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">{translation.subtitle}</h5>
                  </CardHeader>
                  <CardBody className="p-0">
                    {loadingMenus ? (
                      <div className="text-center p-4">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">{translation.spinner}</p>
                      </div>
                    ) : menuData.length === 0 ? (
                      <div className="text-center p-4">
                        <p className="text-muted mb-0">
                          {selectedCompany ? translation.notFound : translation.select}
                        </p>
                      </div>
                    ) : (
                      <div className="menu-tree">
                        {menuData.map((node) => (
                          <TreeNode
                            key={node.id}
                            node={node}
                            onToggle={handleToggleNode}
                            onEdit={handleEdit}
                            onCreate={handleCreate}
                            onDelete={handleDelete}
                            loading={loadingDelete}
                            disabled={!usePermissionEdit || !usePermissionInsert}
                          />
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        isOpen={modalOpen}
        toggle={() => !loadingSubmit && setModalOpen(!modalOpen)}
        size="md"
      >
        <ModalHeader toggle={() => !loadingSubmit && setModalOpen(!modalOpen)}>
          {currentDialog?.mode === 'create' ? translation.create : translation.edit} {currentDialog?.title}
        </ModalHeader>
        <Form>
          <ModalBody>
            {currentDialog?.fields?.map((field) => renderFormField(field))}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setModalOpen(false)}
              disabled={loadingSubmit}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={loadingSubmit || !usePermissionEdit || !usePermissionInsert}
            >
              {loadingSubmit ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Salvando...
                </>
              ) : (
                currentDialog?.mode === 'create' ? translation.create : translation.save
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ConfirmationModal
        isOpen={confirmModalOpen}
        toggle={() => !loadingDelete && setConfirmModalOpen(false)}
        title={translation.confirmDelete}
        message={`${translation.modalMessage}  "${itemToDelete?.nome}"?`}
        warning={
          itemToDelete?.children && itemToDelete.children.length > 0
            ? translation.modalWarning
            : null
        }
        onConfirm={confirmDelete}
        confirmText={translation.confirmDelete}
        cancelText={translation.modalCancelButton}
        loading={loadingDelete}
      />
    </React.Fragment>
  );
};

MenuManagement.propTypes = {};

const mapStateToProps = ({ Login }) => {
  const { user } = Login;
  return { user };
};

export default withTranslation()(connect(mapStateToProps, {})(withRouter(MenuManagement)));